import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Property, Category } from '../types';
import apiService from '../services/api';
import { PropertyCard } from '../components/property/PropertyCard';
import { PropertyCardSkeleton } from '../components/property/PropertyCardSkeleton';
import { Pagination } from '../components/ui/Pagination';
import { Filter, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CategoryPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [properties, setProperties] = useState<Property[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchCategoryAndProperties();
    }
  }, [slug, page, selectedStatus]);

  const fetchCategoryAndProperties = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all categories and find the matching one by slug (simple approach)
      const categoriesResponse = await apiService.getCategories();
      if (!categoriesResponse.success || !categoriesResponse.data) {
        setError('Failed to load categories');
        return;
      }

      // Find category by slug
      const categoryData = categoriesResponse.data.find(cat => 
        cat.slug === slug || cat.name.toLowerCase().replace(/\s+/g, '-') === slug
      );

      if (!categoryData) {
        setError('Category not found');
        return;
      }

      setCategory(categoryData);

      // Fetch properties for this category
      const params: any = { 
        page,
        category_id: categoryData.id 
      };
      
      if (selectedStatus !== 'all') {
        params.status = selectedStatus;
      }

      const propertiesResponse = await apiService.getProperties(params);
      if (propertiesResponse.data) {
        setProperties(propertiesResponse.data.data);
        setTotalPages(propertiesResponse.data.pagination.total_pages);
      }
    } catch (err) {
      setError('Failed to fetch category data');
      console.error('Error fetching category:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property =>
    property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 inline-block">
            {error}
          </div>
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
        <Link to="/" className="hover:text-primary">Home</Link>
        <span>/</span>
        <Link to="/properties" className="hover:text-primary">Properties</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">
          {category?.name || (slug && slug.charAt(0).toUpperCase() + slug.slice(1))}
        </span>
      </div>

      {/* Category Header */}
      <div className="flex flex-col gap-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              {category && (
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
              )}
              {category?.name || 'Category Properties'}
            </h1>
            {category?.description && (
              <p className="text-gray-600 mt-2">{category.description}</p>
            )}
          </div>
          
          {/* Search Bar */}
          <div className="relative flex-1 sm:flex-none sm:w-80">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search in this category..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Compact Filters Between Title and Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Mobile Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="sm:hidden flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md text-sm text-gray-700 font-medium"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>

          {/* Desktop/Mobile Filters */}
          <div className={`w-full sm:w-auto flex flex-col sm:flex-row gap-2 sm:gap-3 ${showFilters ? 'block' : 'hidden sm:flex'}`}>
            {/* Status Filter - Compact */}
            <div className="sm:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setPage(1);
                }}
                className="w-full text-sm px-3 py-1.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-primary focus:border-primary transition-colors bg-white"
              >
                <option value="all">All Status</option>
                <option value="for_sale">For Sale</option>
                <option value="for_rent">For Rent</option>
                <option value="residential_plots">Residential Plots</option>
                <option value="commercial_plots">Commercial Plots</option>
                <option value="agricultural_land">Agricultural Land</option>
                <option value="sold">Sold</option>
                <option value="rented">Rented</option>
                <option value="under_construction">Under Construction</option>
              </select>
            </div>

            {/* Quick Actions - Compact */}
            <div className="flex gap-2">
              <Link
                to="/properties"
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                All Properties
              </Link>

              {(selectedStatus !== 'all' || searchTerm) && (
                <button
                  onClick={() => {
                    setSelectedStatus('all');
                      setSearchTerm('');
                    setPage(1);
                  }}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      

      {/* Active Filters Display */}
      {(selectedStatus !== 'all') && (
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="text-sm text-gray-600">Active filters:</span>
          {selectedStatus !== 'all' && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm">
              Status: {selectedStatus.replace('_', ' ')}
            </span>
          )}
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <div className="text-center text-gray-600 mb-6">
          <span className="font-medium">{filteredProperties.length} properties found</span>
        </div>
      )}

      {/* Properties Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
            <PropertyCardSkeleton key={n} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard 
                key={property.id} 
                property={property}
                showFeaturedBadge={property.is_featured}
              />
            ))}
          </div>
          
          {filteredProperties.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">No properties found</h3>
              <p className="mt-2 text-gray-500">
                {searchTerm 
                  ? "Try adjusting your search terms" 
                  : `No properties available in ${category?.name || 'this category'} at the moment`
                }
              </p>
              <div className="mt-4 space-x-4">
                <Link
                  to="/properties"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  View All Properties
                </Link>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && filteredProperties.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};